import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { BadgeCheck, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { explain } from '@/shared/api/problem'
import { ErpTable, MetricCard, Td, Th } from '@/components/erp/primitives'
import { EmptyState, ErrorStrip, LoadingRows, Note } from '@/shared/ui/states'
import { fmtDateTime, fmtNumber } from '@/shared/lib/format'
import { useElectionContext } from '@/shared/election/context'
import { NoElectionSelected } from '@/shared/election/ElectionSelector'
import {
  certifyReturn, listReturns, returnVersions, verifyReturn, type TallyReturn,
} from './api'
import { ReturnStatusChip, formLabel, seatLabel } from './vocab'

/**
 * The declaration desk: what is verified and awaiting certification, and what has been
 * declared.
 *
 * ## Certifying is terminal and the button says so
 *
 * Nothing moves out of certified. A result that turns out to be wrong afterwards is replaced
 * by a correction — a new version, with a reason — and the certified row stays exactly as it
 * was declared, forever. That is the difference between a count that can explain itself and
 * one that cannot, so the confirmation is deliberate rather than a convenience.
 *
 * ## Verification does NOT happen here, despite the button
 *
 * This page fetches `VERIFIED` and `CERTIFIED` only, so a return still awaiting verification
 * is never in `rows` and the verify branch below is unreachable from it. It is kept because
 * a corrected version lands back at `SUBMITTED` and may be opened from a link, not because
 * this is the queue. **The queue lives on the Returns page**, which lists `SUBMITTED` and is
 * the only screen that can start a verification.
 *
 * That gap is worth naming: for a while nothing anywhere fetched `SUBMITTED`, so no return
 * could be verified through the UI at all, and therefore nothing could ever reach a board.
 * The boards were right and looked broken.
 */
export function CertificatesPage() {
  const { electionId } = useElectionContext()
  const [params, setParams] = useSearchParams()
  const showCertified = params.get('view') !== 'pending'

  const [rows, setRows] = useState<TallyReturn[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [chain, setChain] = useState<TallyReturn[] | null>(null)

  const load = useCallback(async () => {
    if (!electionId) { setRows([]); return }
    try {
      setError(null)
      setRows(await listReturns({
        electionId, status: showCertified ? 'CERTIFIED' : 'VERIFIED', size: 300,
      }))
    } catch (err) {
      setError(explain(err))
      setRows([])
    }
  }, [electionId, showCertified])

  useEffect(() => { void load() }, [load])

  if (!electionId) return <NoElectionSelected what="A certificate register" />

  const act = async (id: string, what: 'verify' | 'certify') => {
    if (what === 'certify' && !window.confirm(
      'Certifying declares this result. It cannot be moved afterwards — a mistake is '
      + 'corrected by filing a new version with a reason, and this row stays exactly as '
      + 'declared. Continue?')) return
    setBusyId(id)
    setError(null)
    try {
      if (what === 'verify') await verifyReturn(id)
      else await certifyReturn(id)
      await load()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusyId(null)
    }
  }

  const openChain = async (id: string) => {
    setChain(null)
    try {
      setChain(await returnVersions(id))
    } catch (err) {
      setError(explain(err))
    }
  }

  const certifiedCount = rows?.length ?? 0

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-erp-base font-semibold">Certificates</h2>
          <p className="text-erp-xs text-muted-foreground mt-0.5">
            Declared results, and the verified returns waiting to be declared.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {(['pending', 'certified'] as const).map((v) => (
            <button key={v} type="button"
              onClick={() => setParams((p) => { p.set('view', v); return p }, { replace: true })}
              className={cn('h-8 px-2.5 rounded-md border text-erp-xs',
                (v === 'certified') === showCertified
                  ? 'border-chocolate bg-chocolate-soft/40 text-chocolate font-medium'
                  : 'border-input text-muted-foreground hover:text-foreground')}>
              {v === 'pending' ? 'Awaiting certification' : 'Declared'}
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
        <MetricCard label={showCertified ? 'Declared' : 'Ready to declare'}
          value={fmtNumber(certifiedCount)} />
      </div>

      <ErrorStrip message={error} className="rounded-md border" />

      {rows !== null && rows.length === 0 && !error && (
        <EmptyState
          title={showCertified ? 'Nothing has been declared yet' : 'Nothing is ready to declare'}
          hint={showCertified
            ? 'A result appears here once somebody has certified it. Certification is terminal.'
            : 'A return has to be verified by somebody other than whoever entered it before it '
              + 'can be declared.'} />
      )}

      {(rows === null || rows.length > 0) && (
        <ErpTable head={(
          <>
            <Th>Place</Th>
            <Th>Race</Th>
            <Th>Status</Th>
            <Th className="text-right">Valid</Th>
            <Th className="text-right">Turnout</Th>
            <Th>Signed</Th>
            <Th />
          </>
        )}>
          {rows === null ? <LoadingRows colSpan={7} />
            : rows.map((r) => (
              <tr key={r.id}>
                <Td>{r.unitName ?? '—'}</Td>
                <Td>
                  {seatLabel(r.seatTypeCode)}
                  <span className="block text-erp-xs text-muted-foreground">
                    {formLabel(r.formTypeCode)}
                    {r.versionNo > 1 && ` · version ${r.versionNo}`}
                  </span>
                </Td>
                <Td>
                  <ReturnStatusChip code={r.statusCode} />
                  {r.pendingVariances > 0 && (
                    <span className="block mt-0.5 text-erp-xs text-amber-700 dark:text-amber-400">
                      {r.pendingVariances} unsettled disagreement
                      {r.pendingVariances === 1 ? '' : 's'}
                    </span>
                  )}
                </Td>
                <Td className="text-right tabular-nums font-medium">{fmtNumber(r.validVotes)}</Td>
                <Td className="text-right tabular-nums">
                  {r.turnoutPct == null ? '—' : `${r.turnoutPct}%`}
                </Td>
                <Td className="text-erp-xs text-muted-foreground">
                  {r.certifiedAt ? fmtDateTime(r.certifiedAt)
                    : r.verifiedAt ? `verified ${fmtDateTime(r.verifiedAt)}` : '—'}
                </Td>
                <Td>
                  <div className="flex items-center gap-2">
                    {busyId === r.id && <Loader2 className="h-3 w-3 animate-spin" />}
                    {r.statusCode === 'VERIFIED' && (
                      <button type="button" disabled={busyId !== null}
                        onClick={() => void act(r.id, 'certify')}
                        className="inline-flex items-center gap-1 text-erp-xs text-chocolate hover:underline">
                        <BadgeCheck className="h-3 w-3" /> Certify
                      </button>
                    )}
                    {r.versionNo > 1 && (
                      <button type="button" onClick={() => void openChain(r.id)}
                        className="text-erp-xs text-muted-foreground hover:text-foreground hover:underline">
                        History
                      </button>
                    )}
                  </div>
                </Td>
              </tr>
            ))}
        </ErpTable>
      )}

      {chain && (
        <div className="erp-card p-3">
          <h3 className="text-erp-sm font-medium mb-1">Correction history</h3>
          <p className="text-erp-xs text-muted-foreground mb-2">
            Newest first. Each version keeps the figures it was filed with — this is what a
            dispute is argued over.
          </p>
          <ErpTable head={(
            <>
              <Th className="w-16 text-right">Version</Th>
              <Th className="text-right">Valid</Th>
              <Th>Status</Th>
              <Th>Reason it replaced the one before</Th>
              <Th>Filed</Th>
            </>
          )}>
            {chain.map((v) => (
              <tr key={v.id}>
                <Td className="text-right tabular-nums">{v.versionNo}</Td>
                <Td className="text-right tabular-nums">{fmtNumber(v.validVotes)}</Td>
                <Td><ReturnStatusChip code={v.statusCode} /></Td>
                <Td className="text-erp-xs">{v.correctionReason ?? '— first filing —'}</Td>
                <Td className="text-erp-xs text-muted-foreground">
                  {fmtDateTime(v.createdAt)}
                </Td>
              </tr>
            ))}
          </ErpTable>
        </div>
      )}

      <Note tone="info">
        Certifying is <strong>terminal</strong>. A declared result that turns out to be wrong
        is replaced by a correction — a new version carrying a reason — and the row that was
        declared stays exactly as it was.
      </Note>
    </div>
  )
}
